import React, { useEffect, useMemo, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Eraser, AlertTriangle, RefreshCw, Loader2, ShieldAlert, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Produksjonsopprydding: fjerner testdata som fulgte med databasen til produksjon.
 * Alt er destruktivt og permanent – derfor forhåndsvisning, eksplisitt valg og bekreftelsesord.
 */
export default function AdminCleanupPage() {
  const [pv, setPv] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [opts, setOpts] = useState({ remove_other_admins: true, reset_bankid: true, reset_smtp: false, set_app_url: true, purge_orphans: true });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [word, setWord] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const origin = window.location.origin;

  const load = async () => {
    setPv(null);
    try {
      const r = await api.get("/admin/cleanup/preview");
      setPv(r.data);
      // Forvalg: alle deaktiverte kontoer (det er slik admin har markert testbrukere)
      setSelected(new Set(r.data.users.filter((u) => !u.is_active).map((u) => u.id)));
    } catch (e) { toast.error(errorMessage(e)); setPv({ users: [], other_admins: [], settings: {}, totals: {}, error: true }); }
  };
  useEffect(() => { load(); }, []);

  const users = pv?.users || [];
  const allSelected = users.length > 0 && users.every((u) => selected.has(u.id));
  const toggleAll = (v) => setSelected(v ? new Set(users.map((u) => u.id)) : new Set());
  const toggleOne = (id, v) => setSelected((prev) => { const n = new Set(prev); if (v) n.add(id); else n.delete(id); return n; });

  const actions = useMemo(() => {
    const out = [];
    if (selected.size) out.push(`${selected.size} brukerkonto${selected.size === 1 ? "" : "er"} slettes permanent med alle regninger, fakturaer, bilag, virksomheter og passordhvelv`);
    if (opts.remove_other_admins && pv?.other_admins?.length) out.push(`${pv.other_admins.length} andre admin-konto${pv.other_admins.length === 1 ? "" : "er"} slettes (${pv.other_admins.map((a) => a.email).join(", ")})`);
    if (opts.reset_bankid) out.push("BankID slås av og mock-oppsettet slettes");
    if (opts.reset_smtp) out.push("SMTP-oppsettet nullstilles (server, bruker, passord, avsender)");
    if (opts.set_app_url) out.push(`Offentlig adresse settes til ${origin}`);
    if (opts.purge_orphans) out.push("Foreldreløse data og midlertidige tabeller (cache, påloggingstilstand) tømmes");
    return out;
  }, [selected, opts, pv, origin]);

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/cleanup/run", {
        confirm: word,
        delete_user_ids: [...selected],
        remove_other_admins: opts.remove_other_admins,
        reset_bankid: opts.reset_bankid,
        reset_smtp: opts.reset_smtp,
        app_url: opts.set_app_url ? origin : null,
        purge_orphans: opts.purge_orphans,
      });
      setResult(r.data);
      setConfirmOpen(false);
      setWord("");
      toast.success(`Opprydding fullført: ${r.data.deleted_users.length} brukere og ${r.data.deleted_admins.length} admin-kontoer slettet`);
      load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setRunning(false); }
  };

  const s = pv?.settings || {};
  const nothingToDo = actions.length === 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Opprydding"
        description="Fjern testdata som fulgte med til produksjon. Alt her er permanent – ingen tombstones, ingen angre."
        actions={<Button variant="outline" onClick={load} data-testid="cleanup-refresh"><RefreshCw className="h-4 w-4 mr-2" /> Oppdater</Button>}
      />

      <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 flex gap-3" data-testid="cleanup-warning">
        <ShieldAlert className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium">Destruktiv operasjon</p>
          <p className="text-muted-foreground">Valgte kontoer slettes med alle data, inkludert dokumenter i filarkivet. Kontoen du er logget inn med kan ikke slettes. Handlingen logges i revisjonsloggen.</p>
        </div>
      </div>

      {!pv ? <Skeleton className="h-96" /> : (
        <>
          <Card data-testid="cleanup-users-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-base">1. Brukerkontoer som skal slettes</CardTitle>
                  <CardDescription>Deaktiverte kontoer er forhåndsvalgt. Du ser kun antall – aldri innhold.</CardDescription>
                </div>
                <Badge variant="outline" data-testid="cleanup-selected-count">{selected.size} av {users.length} valgt</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table data-testid="cleanup-users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 pl-4"><Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Velg alle" data-testid="cleanup-select-all" /></TableHead>
                    <TableHead>Bruker</TableHead>
                    <TableHead className="hidden md:table-cell">Opprettet</TableHead>
                    <TableHead className="text-right">Regn.</TableHead>
                    <TableHead className="text-right hidden sm:table-cell">Fakt.</TableHead>
                    <TableHead className="text-right hidden sm:table-cell">Bilag</TableHead>
                    <TableHead className="text-right hidden lg:table-cell">Virks.</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">Ingen brukerkontoer i databasen</TableCell></TableRow> : users.map((u) => (
                    <TableRow key={u.id} className={cn("h-14", selected.has(u.id) && "bg-destructive/5")} data-testid={`cleanup-user-row-${u.id}`}>
                      <TableCell className="pl-4"><Checkbox checked={selected.has(u.id)} onCheckedChange={(v) => toggleOne(u.id, !!v)} aria-label={`Velg ${u.email}`} data-testid={`cleanup-user-check-${u.id}`} /></TableCell>
                      <TableCell className="max-w-[180px] sm:max-w-none"><p className="font-medium truncate">{u.full_name || "–"}</p><p className="text-xs text-muted-foreground truncate">{u.email}</p></TableCell>
                      <TableCell className="hidden md:table-cell text-sm tnum">{formatDateTime(u.created_at)}</TableCell>
                      <TableCell className="text-right tnum">{u.invoices}</TableCell>
                      <TableCell className="text-right tnum hidden sm:table-cell">{u.sales_invoices}</TableCell>
                      <TableCell className="text-right tnum hidden sm:table-cell">{u.attachments}</TableCell>
                      <TableCell className="text-right tnum hidden lg:table-cell">{u.workspaces}</TableCell>
                      <TableCell>
                        {u.deleted_at ? <Badge variant="outline">Slettet</Badge> : u.is_active ? <span className="text-xs font-medium text-success">Aktiv</span> : <span className="text-xs font-medium text-destructive">Deaktivert</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card data-testid="cleanup-options-card">
            <CardHeader><CardTitle className="text-base">2. Innstillinger og andre kontoer</CardTitle><CardDescription>Nåværende tilstand vises til høyre.</CardDescription></CardHeader>
            <CardContent className="space-y-2">
              <OptionRow
                id="remove_other_admins" checked={opts.remove_other_admins} onChange={(v) => setOpts({ ...opts, remove_other_admins: v })}
                title="Slett andre admin-kontoer" disabled={!pv.other_admins?.length}
                desc={pv.other_admins?.length ? `${pv.other_admins.length} stk: ${pv.other_admins.map((a) => a.email).join(", ")}` : "Ingen andre admin-kontoer"}
              />
              <OptionRow
                id="reset_bankid" checked={opts.reset_bankid} onChange={(v) => setOpts({ ...opts, reset_bankid: v })}
                title="Slå av BankID og slett mock-oppsett"
                desc={s.bankid_enabled ? `BankID er PÅ mot ${s.bankid_issuer || "ukjent issuer"} – vanlige brukere kan ikke logge inn med passord` : "BankID er av"}
                warn={s.bankid_enabled}
              />
              <OptionRow
                id="reset_smtp" checked={opts.reset_smtp} onChange={(v) => setOpts({ ...opts, reset_smtp: v })}
                title="Nullstill SMTP"
                desc={s.smtp_host ? `Server ${s.smtp_host}:${s.smtp_port}${s.smtp_password_set ? ", passord lagret" : ""}` : `Ingen server satt${s.smtp_password_set ? ", men et gammelt passord er lagret" : ""}`}
                warn={!s.smtp_host && s.smtp_password_set}
              />
              <OptionRow
                id="set_app_url" checked={opts.set_app_url} onChange={(v) => setOpts({ ...opts, set_app_url: v })}
                title={`Sett offentlig adresse til ${origin}`}
                desc={s.app_url ? `Nå: ${s.app_url}` : "Ikke satt"}
                warn={!!s.app_url && s.app_url !== origin}
              />
              <OptionRow
                id="purge_orphans" checked={opts.purge_orphans} onChange={(v) => setOpts({ ...opts, purge_orphans: v })}
                title="Tøm foreldreløse data og midlertidige tabeller"
                desc={`Filarkiv: ${pv.totals?.files ?? 0} filer · Revisjonslogg: ${pv.totals?.audit_logs ?? 0} rader`}
              />
            </CardContent>
          </Card>

          <Card className="border-destructive/40" data-testid="cleanup-summary-card">
            <CardHeader><CardTitle className="text-base">3. Dette skjer</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {nothingToDo ? <p className="text-sm text-muted-foreground" data-testid="cleanup-nothing">Ingen handlinger valgt.</p> : (
                <ul className="space-y-1.5 text-sm" data-testid="cleanup-actions-list">
                  {actions.map((a) => <li key={a} className="flex gap-2"><AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" /> <span>{a}</span></li>)}
                </ul>
              )}
              <Button variant="destructive" disabled={nothingToDo} onClick={() => setConfirmOpen(true)} data-testid="cleanup-open-confirm"><Eraser className="h-4 w-4 mr-2" /> Kjør opprydding …</Button>
            </CardContent>
          </Card>

          {result ? (
            <Card data-testid="cleanup-result-card">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-success" /> Siste opprydding</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-2">
                <p><span className="text-muted-foreground">Slettede brukere:</span> {result.deleted_users.length ? result.deleted_users.join(", ") : "ingen"}</p>
                <p><span className="text-muted-foreground">Slettede admin-kontoer:</span> {result.deleted_admins.length ? result.deleted_admins.join(", ") : "ingen"}</p>
                <p><span className="text-muted-foreground">Innstillinger:</span> {result.settings.length ? result.settings.join(", ") : "ingen"}</p>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {Object.entries(result.counts || {}).filter(([, v]) => v > 0).map(([k, v]) => <Badge key={k} variant="secondary" className="tnum">{k}: {v}</Badge>)}
                </div>
              </CardContent>
            </Card>
          ) : null}
        </>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={(o) => { if (!running) { setConfirmOpen(o); if (!o) setWord(""); } }}>
        <AlertDialogContent className="bg-card" data-testid="cleanup-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Bekreft permanent sletting</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <ul className="list-disc pl-5 space-y-1 text-sm">{actions.map((a) => <li key={a}>{a}</li>)}</ul>
                <p className="text-sm">Dette kan ikke angres. Skriv <strong>SLETT</strong> for å bekrefte.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="cleanup-word">Bekreftelsesord</Label>
            <Input id="cleanup-word" value={word} onChange={(e) => setWord(e.target.value)} placeholder="SLETT" autoComplete="off" data-testid="cleanup-confirm-input" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={running} data-testid="cleanup-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" disabled={running || word.trim().toUpperCase() !== "SLETT"} onClick={(e) => { e.preventDefault(); run(); }} data-testid="cleanup-confirm-run">
              {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eraser className="h-4 w-4 mr-2" />} Slett permanent
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const OptionRow = ({ id, checked, onChange, title, desc, warn, disabled }) => (
  <div className={cn("flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5", warn ? "status-forfaller_snart" : "bg-secondary/50", disabled && "opacity-60")} data-testid={`cleanup-opt-${id}`}>
    <div className="min-w-0">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground break-words">{desc}</p>
    </div>
    <Switch checked={checked && !disabled} onCheckedChange={onChange} disabled={disabled} aria-label={title} data-testid={`cleanup-switch-${id}`} />
  </div>
);
